import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { AribaApiType } from '~/enums/AribaApis';
import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';

import { API_NAMES } from '../../constants';

function Realm() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const activeSyncPair = useSelector(
    slices.integrations.selectors.selectActiveSyncPair,
  );

  const stageProperties = activeSyncPair[ManageSyncPairStageType.Realm] ?? {};
  const isTokenReauthenticate = !!activeSyncPair?.isTokenReauthenticate;
  // if isReauth, the data is in the providerOptions
  const providerOptions = activeSyncPair.providerOptions ?? {};
  const { realm, location } = isTokenReauthenticate
    ? providerOptions
    : stageProperties;
  const { errors } = stageProperties ?? {};

  const {
    data: locationsRes,
    isSuccess: isGetLocationsSuccess,
    isFetching: isFetchingLocations,
  } = api.endpoints.getAribaLocations.useQuery(undefined);

  const locations = useMemo(() => {
    if (isGetLocationsSuccess) {
      return Object.keys(locationsRes).map((key) => {
        return {
          label: locationsRes[key],
          value: key,
        };
      });
    } else {
      return [];
    }
  }, [locationsRes, isGetLocationsSuccess]);

  const setRealm = (realm) => {
    const updatedValues = { ...stageProperties, realm, errors: {} };
    const updatedProperties = { ...providerOptions, realm };
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        providerOptions: updatedProperties,
        [ManageSyncPairStageType.Realm]: updatedValues,
      }),
    );
  };

  const onSelectLocation = (location) => {
    const locationName = locationsRes[location];
    const updatedProperties = { ...providerOptions, location };
    const updatedValues = {
      ...stageProperties,
      location,
      locationName,
      errors: {},
    };
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        providerOptions: updatedProperties,
        [ManageSyncPairStageType.Realm]: updatedValues,
      }),
    );
  };

  const getError = (apiType) => {
    if (!errors) {
      return null;
    }
    const errorData = errors[apiType];
    if (errorData && 'error' in errorData) {
      return errorData.error;
    }
    return null;
  };

  return (
    <Layout preset="form-fields">
      <FormField
        required
        label={intl.formatMessage({
          id: 'Realm.realmIdLabel',
          defaultMessage: 'Realm ID',
          description: 'Label for realm ID input field',
        })}
        placeholder={intl.formatMessage({
          id: 'Realm.realmIdPlaceholder',
          defaultMessage: 'Enter a SAP Ariba Realm ID',
          description: 'Placeholder text for realm ID input',
        })}
        value={realm}
        onChange={setRealm}
        input={TextInput}
        error={getError(API_NAMES[AribaApiType.REALM])}
      />
      <FormField
        required
        label={intl.formatMessage({
          id: 'Realm.dataCenterLocationLabel',
          defaultMessage: 'Data Center Location',
          description: 'Label for data center location select field',
        })}
        value={location}
        inputProps={{
          options: locations,
          isMulti: false,
          isLoading: isFetchingLocations,
        }}
        onChange={onSelectLocation}
        input={Select}
        error={getError(API_NAMES[AribaApiType.REGION])}
      />
    </Layout>
  );
}

export default Realm;
